import { ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button, ButtonType, Tooltip, TooltipPosition } from "shared/components"

import { Result } from "./schema"
import { AnswerOption, AnswerOptions, Section, ViewModel } from "./useViewModel"
import { WizardResult } from "./WizardResult"

type WizardPageViewProps = {
    state: ViewModel
    onComplete?: () => void
    onNextSection?: (section: string) => void
    onPreviousSection?: (section: string) => void
    onWizardStateChange?: (sectionIndex: number, questionIndex: number) => void
    color?: "primary" | "tertiary"
    showSectionResults?: boolean
}

type PageOptionsProps = {
    answers: AnswerOptions
    multiple?: boolean
    color?: "primary" | "tertiary"
    onSubmit: (answers: string[]) => void
}

type PageProps = {
    state: ViewModel
    section: Section
    sectionIndex: number
    questionIndex: number
    color?: "primary" | "tertiary"
}

// Helper function to get all questions for a section
const getSectionQuestions = (section: Section) => [
    { question: section.question, answers: section.answers },
    ...section.extraQuestions.map((eq) => ({
        question: eq.question,
        answers: eq.answers,
    })),
]

// Memoized component for better performance
const PageOptions = ({
    multiple = false,
    answers,
    color = "primary",
    onSubmit,
}: PageOptionsProps) => {
    const { t } = useTranslation()

    // Initialize selected state based on current answers
    const getInitialSelected = useCallback(() => {
        if (multiple) {
            return answers.options
                .filter((option) => option.state === "checked")
                .map((option) => option.id)
        } else {
            const checkedOption = answers.options.find(
                (option) => option.state === "checked",
            )
            return checkedOption ? [checkedOption.id] : []
        }
    }, [answers.options, multiple])

    const [selected, setSelected] = useState<string[]>(getInitialSelected)

    const handleSelect = useCallback(
        (option: string) => {
            let newSelected: string[]

            if (multiple) {
                if (selected.includes(option)) {
                    newSelected = selected.filter((id) => id !== option)
                } else {
                    newSelected = [...selected, option]
                }
            } else {
                newSelected = selected.includes(option) ? [] : [option]
            }

            setSelected(newSelected)
            onSubmit(newSelected)
        },
        [selected, multiple, onSubmit],
    )

    const handleNoneOfTheAbove = useCallback(() => {
        const newSelected: string[] = []
        setSelected(newSelected)
        onSubmit(newSelected)
    }, [onSubmit])

    // Check if "None of the above" should be highlighted
    const isNoneSelected = selected.length === 0 && answers.answered === "yes"

    return (
        <div className="grid auto-rows-fr grid-cols-1 gap-4 sm:gap-8 md:grid-cols-2">
            {answers.options.map((option: AnswerOption) => (
                <button
                    key={option.id}
                    className={`flex cursor-pointer items-center justify-center rounded-md border-2 border-solid p-3 transition-colors sm:p-4 ${
                        option.state === "checked"
                            ? `border-${color} bg-${color}/5`
                            : "border-border-primary hover:border-border-secondary"
                    }`}
                    onClick={() => handleSelect(option.id)}
                >
                    <p className="sm:text-md text-center text-sm font-normal select-none">
                        {option.label}
                    </p>
                </button>
            ))}
            {multiple && (
                <button
                    className={`border-border-primary flex cursor-pointer items-center justify-center rounded-md border-2 border-solid p-3 transition-colors sm:p-4 ${
                        isNoneSelected
                            ? `border-${color} bg-${color}/5`
                            : "border-border-primary hover:border-border-secondary"
                    }`}
                    onClick={handleNoneOfTheAbove}
                >
                    <p className="sm:text-md text-center text-sm font-normal select-none">
                        {t("wizard.none_of_the_above")}
                    </p>
                </button>
            )}
        </div>
    )
}

const Page = ({
    state,
    section,
    sectionIndex,
    questionIndex,
    color,
}: PageProps) => {
    const isCurrentSection = state.sectionIndex === sectionIndex
    const isCurrentQuestion = state.questionIndex === questionIndex
    const isVisible = isCurrentSection && isCurrentQuestion

    if (!isVisible) return null

    // Get the current question and its answers using the shared helper
    const allQuestions = getSectionQuestions(section)
    const currentQuestionData = allQuestions[questionIndex]

    if (!currentQuestionData) {
        console.warn(
            `Question index ${questionIndex} out of bounds for section ${sectionIndex}`,
        )
        return null
    }

    const { question: currentQuestion, answers: currentAnswers } =
        currentQuestionData

    return (
        <section className="my-8 sm:my-16">
            <div className="flex flex-col gap-2">
                <h2 className="text-text-primary text-xl font-bold sm:text-2xl">
                    {currentQuestion.title}
                </h2>
                <div className="flex gap-2 text-left">
                    {currentQuestion.info.description &&
                        currentQuestion.info.description !==
                            currentQuestion.title && (
                            <p className="text-text-secondary sm:text-md mb-4 text-sm sm:mb-6">
                                {currentQuestion.info.description}
                            </p>
                        )}
                    {currentQuestion.info?.tooltip && (
                        <Tooltip
                            text={currentQuestion.info.tooltip}
                            position={TooltipPosition.RIGHT}
                        />
                    )}
                </div>
                {currentQuestion.info?.detail && (
                    <div className="flex gap-2 text-left">
                        <p className="text-text-secondary sm:text-md mb-4 text-sm sm:mb-6">
                            {currentQuestion.info.detail}
                        </p>
                    </div>
                )}
                {currentQuestion.info?.reference && (
                    <div className="flex gap-2 text-left">
                        <p className="text-text-secondary sm:text-md text-sm italic">
                            {currentQuestion.info.reference}
                        </p>
                    </div>
                )}
            </div>
            <div className="my-6 min-h-48 sm:my-8 sm:min-h-64">
                {currentQuestion.type === "oneAnswer" && (
                    <PageOptions
                        answers={currentAnswers}
                        color={color}
                        onSubmit={(answers: string[]) => {
                            state.action.assignSingleAnswer({
                                id: currentQuestion.id,
                                value: answers[0] ?? "",
                            })
                        }}
                    />
                )}
                {currentQuestion.type === "anyThatApply" && (
                    <PageOptions
                        multiple
                        answers={currentAnswers}
                        color={color}
                        onSubmit={(answers: string[]) => {
                            state.action.assignMultiAnswer({
                                id: currentQuestion.id,
                                value: answers,
                            })
                        }}
                    />
                )}
            </div>
        </section>
    )
}

const SectionResultsView = ({
    results,
    sectionTitle,
    color = "tertiary",
    onContinue,
    onPrevious,
}: {
    results: Result[]
    sectionTitle: string
    color?: "primary" | "tertiary"
    onContinue: () => void
    onPrevious: () => void
}) => {
    const { t } = useTranslation()
    const buttonType = useMemo(() => {
        switch (color) {
            case "primary":
                return ButtonType.Primary
            case "tertiary":
                return ButtonType.Tertiary
            default:
                return ButtonType.Tertiary
        }
    }, [color])

    return (
        <div className="flex min-h-84 w-full flex-col justify-between p-2 sm:p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:gap-0">
                <Button
                    buttonType={ButtonType.Secondary}
                    text={t("common.general.previous")}
                    iconLeft={ArrowLeftIcon}
                    enabled={true}
                    onClick={onPrevious}
                    size="medium"
                />
                <Button
                    buttonType={buttonType}
                    text={t("common.general.next")}
                    iconRight={ArrowRightIcon}
                    enabled={true}
                    onClick={onContinue}
                    size="medium"
                />
            </div>
            <section className="my-8 sm:my-16">
                <div className="flex flex-col items-start justify-center gap-2">
                    <h2 className="text-text-primary text-xl font-bold sm:text-2xl">
                        {t("wizard.section.results.title", {
                            sectionTitle,
                        })}
                    </h2>
                </div>
                <div className="my-6 sm:my-8">
                    {results.length > 0 ? (
                        <div className="space-y-4">
                            {results.map((result) => (
                                <WizardResult result={result} key={result.id} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-text-secondary rounded-lg p-8 text-center">
                            <p className="mb-2 text-lg">
                                {t("wizard.section.results.no_results_title", {
                                    sectionTitle,
                                })}
                            </p>
                        </div>
                    )}
                </div>
            </section>
        </div>
    )
}

const PageWizard = ({
    state,
    onComplete = () => {},
    onNextSection = () => {},
    onPreviousSection = () => {},
    onWizardStateChange = () => {},
    color = "tertiary",
    showSectionResults = false,
}: WizardPageViewProps) => {
    const { t } = useTranslation()
    const [showingSectionResults, setShowingSectionResults] = useState(false)

    const buttonType = useMemo(() => {
        switch (color) {
            case "primary":
                return ButtonType.Primary
            case "tertiary":
                return ButtonType.Tertiary
            default:
                return ButtonType.Primary
        }
    }, [color])

    // Memoize current section data
    const currentSection = useMemo(() => {
        return state.sections[state.sectionIndex] || null
    }, [state.sections, state.sectionIndex])

    // Memoize current question data using the shared helper
    const currentQuestionData = useMemo(() => {
        if (!currentSection) return null
        const allQuestions = getSectionQuestions(currentSection)
        return allQuestions[state.questionIndex] || null
    }, [currentSection, state.questionIndex])

    const isCurrentQuestionAnswered = useMemo(() => {
        if (!currentQuestionData) return false
        return currentQuestionData.answers.answered === "yes"
    }, [currentQuestionData])

    const handleNext = useCallback(() => {
        if (!currentSection) {
            console.error("No current section available")
            return
        }

        const allQuestions = getSectionQuestions(currentSection)
        const isLastQuestion = state.questionIndex === allQuestions.length - 1
        const isLastSection = state.sectionIndex === state.sections.length - 1

        if (isLastQuestion) {
            if (isLastSection) {
                onComplete()
            } else {
                // Show section results if enabled and results are available
                if (showSectionResults) {
                    const currentSectionResults = state.getSectionResults(
                        currentSection.question.id,
                    )
                    if (currentSectionResults.length > 0) {
                        setShowingSectionResults(true)
                    } else {
                        // Navigate to next section if no results
                        state.action.nextSection()
                        onNextSection(currentSection.question.title)
                    }
                } else {
                    // Navigate to next section
                    state.action.nextSection()
                    onNextSection(currentSection.question.title)
                }
            }
        } else {
            state.action.nextQuestion()
        }
    }, [currentSection, state, showSectionResults, onComplete, onNextSection])

    const handleContinueToNextSection = useCallback(() => {
        if (!currentSection) {
            console.error("No current section available")
            return
        }

        setShowingSectionResults(false)
        state.action.nextSection()
        onNextSection(currentSection.question.title)
    }, [currentSection, onNextSection, state.action])

    const handlePrevious = useCallback(() => {
        if (state.questionIndex > 0) {
            state.action.previousQuestion()
        } else if (state.sectionIndex > 0) {
            // When going to previous section, first show its results if available
            const previousSection = state.sections[state.sectionIndex - 1]
            if (previousSection && showSectionResults) {
                const previousSectionResults = state.getSectionResults(
                    previousSection.question.id,
                )
                if (previousSectionResults.length > 0) {
                    // Show section results for the previous section
                    setShowingSectionResults(true)
                    // Navigate to previous section but stay on results view
                    state.action.previousSection()
                    if (previousSection) {
                        onPreviousSection(previousSection.question.title)
                    }
                    return
                }
            }

            // If no results or showSectionResults is false, go to last question of previous section
            const previousSectionQuestions =
                getSectionQuestions(previousSection)
            const lastQuestionIndex = previousSectionQuestions.length - 1

            state.action.previousSection()
            state.action.goToQuestion(lastQuestionIndex)
            if (previousSection) {
                onPreviousSection(previousSection.question.title)
            }
            // Notify parent of state change
            onWizardStateChange(state.sectionIndex - 1, lastQuestionIndex)
        }
    }, [state, onPreviousSection, showSectionResults, onWizardStateChange])

    const handleExitSectionResults = useCallback(() => {
        setShowingSectionResults(false)
        // When exiting section results, go to the last question of the current section
        if (currentSection) {
            const allQuestions = getSectionQuestions(currentSection)
            const lastQuestionIndex = allQuestions.length - 1
            state.action.goToQuestion(lastQuestionIndex)
            // Notify parent of state change
            onWizardStateChange(state.sectionIndex, lastQuestionIndex)
        }
    }, [currentSection, state.action, state.sectionIndex, onWizardStateChange])

    if (showingSectionResults) {
        const currentSectionResults = state.getSectionResults(
            currentSection.question.id,
        )
        return (
            <SectionResultsView
                results={currentSectionResults}
                sectionTitle={currentSection.question.title}
                color={color}
                onContinue={handleContinueToNextSection}
                onPrevious={handleExitSectionResults}
            />
        )
    }

    return (
        <div className="flex min-h-84 flex-col justify-between p-2 sm:p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:gap-0">
                <Button
                    buttonType={ButtonType.Secondary}
                    text={t("common.general.previous")}
                    iconLeft={ArrowLeftIcon}
                    enabled={state.questionIndex > 0 || state.sectionIndex > 0}
                    onClick={handlePrevious}
                    size="medium"
                />
                <Button
                    buttonType={buttonType}
                    text={t("common.general.next")}
                    iconRight={ArrowRightIcon}
                    enabled={isCurrentQuestionAnswered}
                    onClick={handleNext}
                    size="medium"
                />
            </div>
            {state.sections.map((section: Section, sectionIndex: number) => (
                <Page
                    key={sectionIndex}
                    state={state}
                    section={section}
                    sectionIndex={sectionIndex}
                    questionIndex={state.questionIndex}
                    color={color}
                />
            ))}
        </div>
    )
}

export default PageWizard
